import React, { lazy, Suspense, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { RouteSkeleton } from '../Components/Skeleton/RouteSkeleton';
import {
  AuthProvider,
  ProtectedRoute,
  useAuth,
  PERMISSIONS,
  INTENTS_ACCESS,
} from '../features/auth';
// Skills declares its own route subtree (guards, permissions and lazy pages
// included); imported from the leaf module so the router doesn't pull the whole
// feature in eagerly and break its lazy chunks.
import { skillsRoutes } from '../features/agent-skills/router';
// The Skills MVP (AGE-269) stays mounted beside the module above so the two can
// be compared live. Both export `skillsRoutes`, hence the alias; they no longer
// collide on the URL because the MVP owns 'skills-mvp' (see its constants.ts).
import { skillsRoutes as skillsMvpRoutes } from '../features/agent-skills-mvp/router';
// Dashboards (SL-1657) declares its own subtree too, and mounts at account level
// rather than under an agent: a dashboard is a first-level object of the account.
import { dashboardsRoutes } from '../features/dashboards/router';
import { learningRoutes } from '../features/learning/router';
import { integrationsRoutes } from '../features/integrations/router';
import { observabilityRoutes } from '../features/agent-observability/router';
// Subagents (VOX-329) mounts under the agent's `myassistant` routes, next to Campaigns.
import { subagentsRoutes } from '../features/agent-subagents/router';
import { AccountProvider } from '../store';
import { AssistantProvider } from '../contexts/AssistantContext';
import { featureFlags } from '../config/featureFlags';
import { agentSetupStartElement, agentSetupStartRoutes } from '../features/agent-setup/startRoutes';
import { createLogger } from '../utils/logger';
import { RequireActiveAccount } from './guards';
// Each route's own skeleton as its Suspense fallback, so the screen shows its
// real shape while its chunk loads instead of RouteSkeleton's generic blocks.
// Imported from the component files, not the feature barrel, so the skeletons
// don't pull the whole Home module into the initial bundle.
import { HomePageSkeleton } from '../features/home/components/HomePageSkeleton/HomePageSkeleton';
import { AgentOverviewSkeleton } from '../features/home/pages/AgentOverviewPage/AgentOverviewSkeleton';
// Eager on purpose: constants only (both modules are dependency-free), not the pages.
import { CAMPAIGN_MANAGE_ROLES } from '../features/campaigns/domain/campaignRoles';
import { AuthPageSkeleton } from '../features/auth/skeletons/AuthPageSkeleton';
import { ResetPasswordSkeleton } from '../features/auth/skeletons/ResetPasswordSkeleton';
import { OrganizationPageSkeleton } from '../features/organizations/skeletons/OrganizationPageSkeleton';
import { OrganizationRouteSkeleton } from '../features/organizations/skeletons/OrganizationRouteSkeleton';
import { AccountsPageSkeleton } from '../features/account/components/AccountSkeleton';
import { AccountSettingsSkeleton } from '../features/account/components/AccountSettings/AccountSettingsSkeleton';
import { ProfileSkeleton } from '../features/account/components/Profile/ProfileSkeleton';
import { DataViewsPageSkeleton } from '../features/data-views/components/DataViewsLoadingSkeleton/DataViewsPageSkeleton';
import { AgentCallsSkeleton } from '../features/inbox-agent-calls/components/AgentCallsSkeleton/AgentCallsSkeleton';
import { InboxChatsSkeleton } from '../features/inbox/components/InboxChatsSkeleton/InboxChatsSkeleton';
import { KnowledgeSkeleton } from '../features/knowledge/components/KnowledgeSkeleton/KnowledgeSkeleton';
import { ChannelsSkeleton } from '../features/agent-setup/channels/components/ChannelsSkeleton/ChannelsSkeleton';
import { EscalationsSkeleton } from '../features/assistants/components/Escalations/EscalationsSkeleton/EscalationsSkeleton';
import { MetricsSkeleton } from '../features/metrics/components/MetricsSkeleton/MetricsSkeleton';
import { EvalsSkeleton } from '../features/evals/components/EvalsSkeleton/EvalsSkeleton';
import { AgentSettingsSkeleton } from '../features/agent-settings/components/AgentSettingsSkeleton/AgentSettingsSkeleton';
import { AccessVerificationSkeleton } from '../features/access-verification/components/AccessVerificationSkeleton/AccessVerificationSkeleton';
import { QaKnowledgeBaseGate } from '../features/qa-agent/QaKnowledgeBaseGate';
import { CampaignRouteSkeleton } from '../features/campaigns/components/CampaignSkeleton/CampaignRouteSkeleton';

const logger = createLogger('router');

const LoginPage = lazy(() =>
  import('../features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const Unauthorized = lazy(() =>
  import('../features/auth/components/Unauthorized').then((m) => ({ default: m.Unauthorized })),
);
const AssistantProcessFlow = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.AssistantProcessFlow,
  })),
);

// Layout Components
const MainLayout = lazy(() =>
  import('../Components/layouts/MainLayout').then((module) => ({ default: module.MainLayout })),
);
const SettingsLayout = lazy(() =>
  import('../Components/layouts/SettingsLayout').then((module) => ({
    default: module.SettingsLayout,
  })),
);
const AccountsLayout = lazy(() =>
  import('../Components/layouts/AccountsLayout').then((module) => ({
    default: module.AccountsLayout,
  })),
);
const MetricsLayout = lazy(() =>
  import('../Components/layouts/MetricsLayout').then((module) => ({
    default: module.MetricsLayout,
  })),
);

// Assistants Components
const AssistantsLayout = lazy(() =>
  import('../Components/Assistants/AssistantsLayout').then((module) => ({
    default: module.AssistantsLayout,
  })),
);
const AssistantDetailLayout = lazy(() =>
  import('../Components/Assistants/AssistantDetailLayout').then((module) => ({
    default: module.AssistantDetailLayout,
  })),
);
const MyAssistantLayout = lazy(() =>
  import('../Components/Assistants/MyAssistantLayout').then((module) => ({
    default: module.MyAssistantLayout,
  })),
);

const AssistantManagement = lazy(() =>
  import('../features/assistant-management/components/AssistantManagement/AssistantManagement').then(
    (module) => ({
      default: module.AssistantManagement,
    }),
  ),
);

// Page Components
const HomePage = lazy(() =>
  import('../features/home').then((module) => ({ default: module.HomePage })),
);
const AgentOverviewPage = lazy(() =>
  import('../features/home').then((module) => ({ default: module.AgentOverviewPage })),
);
const AgentSettingsPage = lazy(() =>
  import('../features/agent-settings').then((module) => ({ default: module.AgentSettingsPage })),
);
// CX-572 — config pages of an EXISTING Real-Time Assistant (the creation wizard lives at
// agents/new/realtime). They mount the shared RTA pages with the agent id from the route.
const RtaProcessesPage = lazy(() =>
  import('../features/agent-setup/pages/RtaProcessesPage').then((module) => ({
    default: module.RtaProcessesPage,
  })),
);
const RtaKnowledgePage = lazy(() =>
  import('../features/agent-setup/pages/RtaKnowledgePage').then((module) => ({
    default: module.RtaKnowledgePage,
  })),
);
const RtaVersionsPage = lazy(() =>
  import('../features/agent-setup/pages/RtaVersionsPage').then((module) => ({
    default: module.RtaVersionsPage,
  })),
);
const RtaTestingPage = lazy(() =>
  import('../features/agent-setup/pages/RtaTestingPage').then((module) => ({
    default: module.RtaTestingPage,
  })),
);
const RtaSettingsPage = lazy(() =>
  import('../features/agent-setup/pages/RtaSettingsPage').then((module) => ({
    default: module.RtaSettingsPage,
  })),
);
const RtaGeneralSettingsPage = lazy(() =>
  import('../features/agent-setup/pages/RtaGeneralSettingsPage').then((module) => ({
    default: module.RtaGeneralSettingsPage,
  })),
);
const RtaTriggersSettingsPage = lazy(() =>
  import('../features/agent-setup/pages/RtaTriggersSettingsPage').then((module) => ({
    default: module.RtaTriggersSettingsPage,
  })),
);
const NotFound = lazy(() =>
  import('../Components/NotFound').then((module) => ({ default: module.NotFound })),
);

// QA Agent — overview real vía agentType 'qa' (ModuleCard/agent-modules.ts, igual
// que RTA/conversacional). Las 7 sub-secciones ya están todas montadas — Sampling
// y Analytics siguen sobre datos mock (sin backend, ADR-0009 §2.2g), el resto
// consume datos reales vía QaAssistantProvider (QaAgentSectionGuard).
// Gateado por REACT_APP_FEATURE_QA_AGENT (mismo patrón que Voice más abajo).
const QaAgentSectionGuard = lazy(() =>
  import('../features/qa-agent').then((module) => ({ default: module.QaAgentSectionGuard })),
);
const QaEvaluationsSection = lazy(() =>
  import('../features/qa-agent').then((module) => ({ default: module.QaEvaluationsSection })),
);
// Inbox/Upload/Evaluations consumen datos reales de QA-Assistant vía
// Wizard de creación de QA Agent — "Quality Agent" en Home's Create new -> Agent
// menu. A diferencia de QaAgentSectionGuard (por-agente, agentType 'qa' ya
// existente), esta ruta es por-cuenta y antecede a la creación del agente.
const QaAgentWizardStepPage = lazy(() =>
  import('../features/qa-agent').then((module) => ({ default: module.QaAgentWizardStepPage })),
);
// Inbox/Upload/Key Moments/Scorecards consumen datos reales de QA-Assistant vía
// QaAssistantProvider (ver QaAgentSectionGuard). Sampling/Analytics todavía no
// tienen backend (ADR-0009 §2.2g) — son presentacionales sobre datos mock, tal
// cual los exporta el paquete, hasta que exista el cliente correspondiente.
const QaInbox = lazy(() =>
  import('@silia-mx/qa-assistant-ui').then((module) => ({ default: module.QaInbox })),
);
const QaUpload = lazy(() =>
  import('@silia-mx/qa-assistant-ui').then((module) => ({ default: module.QaUpload })),
);
// `QaSourceSampling` y no `QaSampling`: el paquete lo declara como el reemplazo
// de `QaSampling` a nivel de página — es el mismo plan de muestreo, ahora detrás
// de un selector de vista que le suma el tab Source (las carpetas de upload, las
// conexiones SFTP y el canal de captura). Montando el viejo, la pantalla se ve
// sin la mitad Source por más que el paquete esté al día.
const QaSourceSampling = lazy(() =>
  import('@silia-mx/qa-assistant-ui').then((module) => ({ default: module.QaSourceSampling })),
);
// Lazy-loads our own wrapper (Feature 11) instead of the package's `QaSettings`
// directly, so the heavy `@silia-mx/qa-assistant-ui` import stays deferred to
// this route exactly like every other `Qa*` lazy import above — the wrapper
// only adds the `billing` slot content and the RBAC check, it doesn't change
// what triggers the code-split.
const QaTrainingPage = lazy(() =>
  import('../features/qa-agent/pages/QaTrainingPage/QaTrainingPage').then((module) => ({
    default: module.QaTrainingPage,
  })),
);
const QaDataViewsPage = lazy(() =>
  import('../features/qa-agent/pages/QaDataViewsPage/QaDataViewsPage').then((module) => ({
    default: module.QaDataViewsPage,
  })),
);
const QaSettingsPage = lazy(() =>
  import('../features/qa-agent/pages/QaSettingsPage/QaSettingsPage').then((module) => ({
    default: module.QaSettingsPage,
  })),
);
const QaAnalytics = lazy(() =>
  import('@silia-mx/qa-assistant-ui').then((module) => ({ default: module.QaAnalytics })),
);

// Recovery and Password Reset Pages
const RecoverAccountPage = lazy(() =>
  import('../features/auth/pages/RecoverAccountPage').then((module) => ({
    default: module.RecoverAccountPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('../features/auth/pages/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const ActivationPage = lazy(() =>
  import('../features/account-activation').then((module) => ({
    default: module.ActivationPage,
  })),
);
const AgentSetupLayout = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.AgentSetupLayout,
  })),
);
const AgentDiscoveryPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.DiscoveryPage,
  })),
);
const AgentProcessPlaceholderPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.ProcessPlaceholderPage,
  })),
);
const CreateAgentStartPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.CreateAgentStartPage,
  })),
);
const ProcessGeneralStepPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.ProcessGeneralStepPage,
  })),
);
// By its own file, not the feature barrel: it carries the skill editor, which no other
// agent-setup page needs (SL-2029).
const ProcessSkillsStepPage = lazy(() =>
  import('../features/agent-setup/pages/ProcessSkillsStepPage').then((module) => ({
    default: module.ProcessSkillsStepPage,
  })),
);
// The pages behind agents/new's start, process and skills routes (SL-2025).
const agentSetupStartPages = {
  Discovery: AgentDiscoveryPage,
  ProcessPlaceholder: AgentProcessPlaceholderPage,
  Start: CreateAgentStartPage,
  ProcessGeneral: ProcessGeneralStepPage,
  ProcessSkills: ProcessSkillsStepPage,
};
const PersonalityStepPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.PersonalityStepPage,
  })),
);
const BehaviorStepPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.BehaviorStepPage,
  })),
);
const LocationStepPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.LocationStepPage,
  })),
);
const AccessStepPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.AccessStepPage,
  })),
);
const AgentSetupStepPlaceholder = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.StepPlaceholderPage,
  })),
);
const ChannelsLayout = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.ChannelsLayout,
  })),
);
const ChannelsPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.ChannelsPage,
  })),
);
const ChannelDetailPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.ChannelDetailPage,
  })),
);

// Settings Pages
const Users = lazy(() =>
  import('../Components/Settings/Users/Users').then((module) => ({ default: module.Users })),
);
const UserDetails = lazy(() =>
  import('../Components/Settings/Details/Details').then((module) => ({ default: module.Details })),
);
const UsageFees = lazy(() =>
  import('../Components/Settings/UsageFees/UsageFees').then((module) => ({
    default: module.UsageFees,
  })),
);

// ✅ Accounts Pages - Updated Imports
const Account = lazy(() =>
  import('../features/account/components/Account').then((module) => ({
    default: module.Account,
  })),
);

const NewAccount = lazy(() =>
  import('../features/account/components/AccountNew/AccountNew').then((module) => ({
    default: module.NewAccount,
  })),
);

const AccountSettings = lazy(() =>
  import('../features/account/components/AccountSettings/AccountSettings').then((module) => ({
    default: module.AccountSettings,
  })),
);

const Profile = lazy(() =>
  import('../features/account/components/Profile/Profile').then((module) => ({
    default: module.Profile,
  })),
);

// Metrics Pages
const Dashboard = lazy(() =>
  import('../Components/Dashboard/Dashboard').then((module) => ({ default: module.Dashboard })),
);

const Inbox = lazy(() =>
  import('../features/inbox/components/Inbox').then((module) => ({ default: module.Inbox })),
);

// Calls Session Pages
const InboxCallsPage = lazy(() =>
  import('../features/inbox-calls/InboxCalls').then((module) => ({
    default: module.InboxCalls,
  })),
);

// Voice-agent phone calls (Twilio channel)
const InboxPhoneCallsPage = lazy(() =>
  import('../features/inbox-phone-calls').then((module) => ({
    default: module.InboxPhoneCallsPage,
  })),
);

// Voice-agent Agent Calls — real backend (Voice module /agent-calls)
const InboxAgentCallsPage = lazy(() =>
  import('../features/inbox-agent-calls').then((module) => ({
    default: module.InboxAgentCallsPage,
  })),
);

const AccountAnalyticsPage = lazy(() =>
  import('../features/dashboard-calls/DashboardCalls').then((module) => ({
    default: module.DashboardCalls,
  })),
);

function InboxLegacyRedirect(): React.ReactElement {
  const { accountId, AssistantID } = useParams<{ accountId: string; AssistantID: string }>();
  const to =
    accountId && AssistantID ? `/accounts/${accountId}/assistants/${AssistantID}/inbox/chats` : '/';
  return <Navigate to={to} replace />;
}

function MyAssistantChannelsRedirect(): React.ReactElement {
  const { accountId, AssistantID } = useParams<{ accountId: string; AssistantID: string }>();
  const assistantId = AssistantID?.trim() ?? '';
  const search = assistantId
    ? `?from=overview&assistantId=${encodeURIComponent(assistantId)}`
    : '?from=overview';
  return <Navigate to={`/accounts/${accountId}/assistants/new/channels${search}`} replace />;
}

const DataViewsPlaceholder = lazy(() =>
  import('../features/data-views/components/DataViewsPlaceholder').then((module) => ({
    default: module.DataViewsPlaceholder,
  })),
);

// My Assistant Pages
const MyAssistantPersonality = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.MyAssistantPersonality,
  })),
);

const CreateAssistantPage = lazy(() =>
  import('../pages/Assistants/CreateAssistantPage').then((module) => ({
    default: module.CreateAssistantPage,
  })),
);

const ChannelsStep = lazy(() =>
  import('../features/assistants/components/NewAgentWizard/steps/ChannelsStep').then((module) => ({
    default: module.ChannelsStep,
  })),
);

const ChannelDetailStep = lazy(() =>
  import('../features/assistants/components/NewAgentWizard/steps/ChannelDetailStep').then(
    (module) => ({
      default: module.ChannelDetailStep,
    }),
  ),
);

const BehaviorStep = lazy(() =>
  import('../features/assistants/components/NewAgentWizard/steps/WizardPlaceholderStep').then(
    (module) => ({
      default: module.BehaviorStep,
    }),
  ),
);

const LocationStep = lazy(() =>
  import('../features/assistants/components/NewAgentWizard/steps/WizardPlaceholderStep').then(
    (module) => ({
      default: module.LocationStep,
    }),
  ),
);

const AccessStep = lazy(() =>
  import('../features/assistants/components/NewAgentWizard/steps/WizardPlaceholderStep').then(
    (module) => ({
      default: module.AccessStep,
    }),
  ),
);

const CostCalculatorPage = lazy(() =>
  import('../pages/CostCalculatorPage').then((module) => ({ default: module.CostCalculatorPage })),
);

const Flows = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.ConversationFlows,
  })),
);
const Knowledge = lazy(() =>
  import('../features/knowledge').then((module) => ({
    default: module.KnowledgePage,
  })),
);
const Appearance = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.Appearance,
  })),
);

const AccessVerification = lazy(() =>
  import('../features/access-verification').then((module) => ({
    default: module.AccessVerification,
  })),
);

const EscalationsPage = lazy(() =>
  import('../features/assistants/components/Escalations/EscalationsPage').then((module) => ({
    default: module.EscalationsPage,
  })),
);

const IntentsPage = lazy(() =>
  import('../features/assistants/components/Intents/IntentsPage').then((module) => ({
    default: module.IntentsPage,
  })),
);

// Queue Management Component
const QueueManagement = lazy(() =>
  import('../pages/QueueManagement/QueueManagementPage').then((module) => ({
    default: module.default,
  })),
);

// Modules Component
const Modules = lazy(() =>
  import('../features/modules/components/Modules').then((module) => ({ default: module.Modules })),
);

const VoiceTestPage = lazy(() =>
  import('../features/voice-test/components/VoiceTestPage').then((module) => ({
    default: module.VoiceTestPage,
  })),
);

// Chatbot Component
const Chatbot = lazy(() =>
  import('../Components/Chatbot/Chatbot').then((module) => ({ default: module.Chatbot })),
);

// Simple component for chatbot with on-premise detection
const ChatbotRoute = () => {
  const { chatbotId } = useParams<{ chatbotId: string }>();
  const isOnPremise = chatbotId === '084e0206-ea8c-45d4-aa47-36ee29908fd3';

  // Memoize URLs to prevent recreation on each render
  const wsUrl = useMemo(
    () =>
      isOnPremise
        ? 'ws://localhost:3001'
        : process.env.REACT_APP_WS_SHARED_URL || 'wss://ws-shared.dev.app.silia.com/',
    [isOnPremise],
  );

  const voiceWsUrl = useMemo(
    () => process.env.REACT_APP_VOICE_AGENT_WS_URL || 'wss://voice-agent.dev.app.silia.com',
    [],
  );

  const urlApi = useMemo(
    () =>
      isOnPremise ? 'http://localhost:3001/chatbot' : process.env.REACT_APP_API_HOST + '/chatbot',
    [isOnPremise],
  );

  // Add a visual indicator
  useEffect(() => {
    if (window && (window as any).document) {
      (window as any).document.title = `Chatbot: ${chatbotId}`;
    }
  }, [chatbotId]);

  return <Chatbot wsUrl={wsUrl} voiceWsUrl={voiceWsUrl} urlApi={urlApi} />;
};

// Smart redirect component that redirects users based on their permissions
const SmartRedirect = () => {
  const { user, loading, atomicPermissionsLoading } = useAuth();
  const { accountId } = useParams();

  // Wait for auth AND permissions before deciding where to land — avoids a
  // premature redirect while GET /me/permissions is still in flight.
  if (loading || atomicPermissionsLoading) {
    return <RouteSkeleton />;
  }

  // Si no hay usuario, algo está mal, redirigir a login
  if (!user?.role) {
    return <Navigate to="/login" replace />;
  }

  // Everyone lands on home. Per-folder permissions (replacing the old /home vs
  // /modules permission split) will gate access at the folder level later; until
  // then we intentionally send users without /home permission to home as well.
  //
  // Absolute on purpose: this renders both as the account index route and as its
  // `*` catch-all. With react-router v7's relative-splat behaviour a relative
  // "home" from the catch-all resolves against the unmatched URL
  // (/accounts/1/foo -> /accounts/1/foo/home), which the catch-all matches again
  // and redirects forever.
  return <Navigate to={`/accounts/${accountId}/home`} replace />;
};

// Documentation Components
const DocsHub = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.DocsHub })),
);

const BackendDocs = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.BackendDocs })),
);

const StorybookDocs = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.StorybookDocs })),
);

const PlatformApiDocs = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.PlatformApiDocs })),
);

// Silia-internal feedback dashboard (Feature 6.3 · AGE-115)
const InternalFeedbackDashboardPage = lazy(() =>
  import('../features/internal-feedback/pages').then((module) => ({
    default: module.InternalFeedbackDashboardPage,
  })),
);

// Silia-internal cross-account assistant-usage panel (Feature 7.7 · AGE-122)
const InternalAssistantUsagePage = lazy(() =>
  import('../features/internal-assistant-usage/pages').then((module) => ({
    default: module.InternalAssistantUsagePage,
  })),
);

// VoiceAgent Component
const VoiceAgentView = lazy(() =>
  import('../features/voiceagent/components').then((module) => ({
    default: module.VoiceAgentView,
  })),
);

// RTA agent configuration — "Real Time Assistant" option in Home's Create new -> Agent menu.
const RTAConfigurationPage = lazy(() =>
  import('../features/agent-setup').then((module) => ({
    default: module.RTAConfigurationPage,
  })),
);

// Metrics Dashboard Component
const MetricsDashboard = lazy(() =>
  import('../features/metrics/components/MetricsDashboard').then((module) => ({
    default: module.MetricsDashboard,
  })),
);

// Evals Dashboard Component
const EvalsDashboard = lazy(() =>
  import('../features/evals/components/EvalsDashboard').then((module) => ({
    default: module.EvalsDashboard,
  })),
);

// Outbound Campaigns listing (VOX-269)
const CampaignsPage = lazy(() =>
  import('../features/campaigns/components/CampaignsPage/CampaignsPage').then((module) => ({
    default: module.CampaignsPage,
  })),
);

// Outbound Campaigns detail (VOX-317)
const CampaignDetailPage = lazy(() =>
  import('../features/campaigns/components/CampaignDetailPage/CampaignDetailPage').then(
    (module) => ({
      default: module.CampaignDetailPage,
    }),
  ),
);

// Outbound Campaigns results table, full page (VOX-319)
const CampaignResultsPage = lazy(() =>
  import('../features/campaigns/components/CampaignResultsPage/CampaignResultsPage').then(
    (module) => ({
      default: module.CampaignResultsPage,
    }),
  ),
);

// Outbound Campaigns wizard (VOX-270)
const CampaignWizardPage = lazy(() =>
  import('../features/campaigns/components/CampaignWizardPage/CampaignWizardPage').then(
    (module) => ({
      default: module.CampaignWizardPage,
    }),
  ),
);

// Organization Components
const OrganizationLayout = lazy(() =>
  import('../features/organizations/OrganizationLayout').then((module) => ({
    default: module.OrganizationLayout,
  })),
);
const OrganizationUsersPage = lazy(() =>
  import('../features/organizations/pages/OrganizationUsersPage').then((module) => ({
    default: module.OrganizationUsersPage,
  })),
);
const OrganizationTeamsPage = lazy(() =>
  import('../features/organizations/pages/OrganizationTeamsPage').then((module) => ({
    default: module.OrganizationTeamsPage,
  })),
);
const OrganizationRolesPage = lazy(() =>
  import('../features/organizations/pages/OrganizationRolesPage').then((module) => ({
    default: module.OrganizationRolesPage,
  })),
);

// Team Overview Component - COMMENTED OUT - NOT READY
//const TeamOverview = lazy(() =>
//   import('../features/team-overview/components/TeamOverview').then((module) => ({ default: module.TeamOverview })),
// );

// Wrapper to extract AssistantID param and pass as prop
const AssistantProcessFlowRoute = () => {
  const { AssistantID } = useParams();
  return <AssistantProcessFlow mode="create" assistantId={AssistantID as string} />;
};

export function AppRouter() {
  // Debug: Log when AppRouter renders
  logger.info('🔄 AppRouter rendering');
  logger.info('🔄 Current location:', window.location.href);

  return (
    <AuthProvider>
      <Suspense fallback={<RouteSkeleton />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <Suspense fallback={<AuthPageSkeleton />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            path="/recoveraccount"
            element={
              <Suspense fallback={<AuthPageSkeleton />}>
                <RecoverAccountPage />
              </Suspense>
            }
          />
          <Route
            path="/resetpassword"
            element={
              <Suspense fallback={<ResetPasswordSkeleton />}>
                <ResetPasswordPage />
              </Suspense>
            }
          />
          <Route path="/account-activation" element={<ActivationPage />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/voiceagent" element={<VoiceAgentView />} />

          {/* Protected documentation routes */}
          <Route
            path="/docs"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superadmin', 'implementador']}>
                <DocsHub />
              </ProtectedRoute>
            }
          />
          {/* Redirect target of the docs-protection edge function for /docs/guides/* */}
          <Route path="/docs/guides" element={<Navigate to="/docs" replace />} />
          <Route
            path="/docs/backend"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superadmin', 'implementador']}>
                <BackendDocs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/storybook"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superadmin', 'implementador']}>
                <StorybookDocs />
              </ProtectedRoute>
            }
          />
          {/* The Postman-based reference was retired: old links land on the platform API. */}
          <Route path="/docs/api-collections" element={<Navigate to="/docs/api" replace />} />
          {/* The platform API generated from the SAM templates (CIB-1305); the hash opens one
              operation, e.g. /docs/api#/Assistant/Assistant_GetChannels. */}
          <Route
            path="/docs/api"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superadmin', 'implementador']}>
                <PlatformApiDocs />
              </ProtectedRoute>
            }
          />

          {/* Silia-internal feedback dashboard (Feature 6.3) — superadmins only,
              mirroring the backend's assertRole on /internal/* */}
          <Route
            path="/internal/assistant-feedback"
            element={
              <ProtectedRoute requiredRoles={['superadmin']}>
                <InternalFeedbackDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Silia-internal cross-account assistant-usage panel (Feature 7.7) —
              superadmins only, mirroring the backend's assertRole on /internal/* */}
          <Route
            path="/internal/assistant-usage"
            element={
              <ProtectedRoute requiredRoles={['superadmin']}>
                <InternalAssistantUsagePage />
              </ProtectedRoute>
            }
          />

          {/* Chatbot route */}
          <Route path="/chat/:chatbotId" element={<ChatbotRoute />} />

          {/* Account-based routes */}
          <Route
            path="/accounts/:accountId"
            element={
              <AccountProvider>
                <AssistantProvider>
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                </AssistantProvider>
              </AccountProvider>
            }
          >
            {/* Home (default route) */}
            <Route index element={<SmartRedirect />} />
            <Route
              path="home"
              element={
                <RequireActiveAccount>
                  <Suspense fallback={<HomePageSkeleton />}>
                    <HomePage />
                  </Suspense>
                </RequireActiveAccount>
              }
            />

            {/* Dashboards (SL-1657): the feature owns its own subtree — flag,
                permissions, error boundary, draft and lazy pages live in
                features/dashboards/router.tsx. Mounted here, beside the Home,
                because a dashboard belongs to the account and not to an agent. */}
            {dashboardsRoutes}

            {/* Learning (CIB-1256): what can be done from the app, the API and MCP, in
                the order of a new account, plus the release notes. Account-scoped like the
                Home; the feature owns its flag and pages in features/learning/router.tsx. */}
            {learningRoutes}

            {/* Integrations (SL-1561): the account's connected providers and its skill tools.
                A draft on mock connections; the feature owns its flag, permission check and
                pages in features/integrations/router.tsx. */}
            {integrationsRoutes}

            {/* Settings section */}
            <Route path="settings" element={<SettingsLayout />}>
              <Route
                index
                element={
                  featureFlags.teams ? (
                    <RequireActiveAccount>
                      <Users />
                    </RequireActiveAccount>
                  ) : (
                    <Navigate to="account" replace />
                  )
                }
              />
              <Route
                path="users"
                element={
                  featureFlags.teams ? (
                    <RequireActiveAccount>
                      <Users />
                    </RequireActiveAccount>
                  ) : (
                    <Navigate to="../account" replace />
                  )
                }
              />
              <Route
                path="user/:userId"
                element={
                  featureFlags.teams ? (
                    <RequireActiveAccount>
                      <UserDetails />
                    </RequireActiveAccount>
                  ) : (
                    <Navigate to="../account" replace />
                  )
                }
              />
              <Route
                path="account"
                element={
                  <ProtectedRoute requiredPermission={PERMISSIONS.account.information.view}>
                    <Suspense fallback={<AccountSettingsSkeleton />}>
                      <AccountSettings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="usage-fees"
                element={
                  <ProtectedRoute requiredPermission={PERMISSIONS.account.billing.view}>
                    <UsageFees />
                  </ProtectedRoute>
                }
              />
              <Route path="billing" element={<div>Billing Page</div>} />
              <Route path="authentication" element={<div>Authentication Page</div>} />
              <Route path="api-access" element={<div>API Access Page</div>} />
              {/* TODO(perms): no atomic permission for the cost calculator exists
                  in the catalog — pending product decision. Role-gated for now. */}
              <Route
                path="calculator-cost"
                element={
                  <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
                    <CostCalculatorPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Organization section */}
            <Route
              path="organization"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.organization.users.view}>
                  {/* The layout is lazy too: without its own boundary, loading it
                      would suspend the app-wide one and blank the app chrome. */}
                  <Suspense fallback={<OrganizationRouteSkeleton />}>
                    <OrganizationLayout />
                  </Suspense>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="users" replace />} />
              <Route
                path="users"
                element={
                  <Suspense fallback={<OrganizationPageSkeleton tab="users" />}>
                    <OrganizationUsersPage />
                  </Suspense>
                }
              />
              {/* Same story as Roles below: the shell only asks for users.view, so
                  Teams needs its own guard or a Supervisor could open it and hit the
                  403 that Teams/infrastructure/.../getTeamsByAccount already returns. */}
              <Route
                path="teams"
                element={
                  <ProtectedRoute
                    requiredPermission={PERMISSIONS.organization.teams.view}
                    denyBehavior="inline"
                  >
                    <Suspense fallback={<OrganizationPageSkeleton tab="teams" />}>
                      <OrganizationTeamsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              {/* Roles needs its own guard: the section shell above only asks for
                  organization.users.view, so without this a Supervisor or Operator
                  could open the Roles tab and see the account's whole role catalog.
                  `inline` keeps them inside the Organization shell (tabs, sidebar)
                  instead of ejecting them to /unauthorized. */}
              <Route
                path="roles"
                element={
                  <ProtectedRoute
                    requiredPermission={PERMISSIONS.organization.roles.view}
                    denyBehavior="inline"
                  >
                    <Suspense fallback={<OrganizationPageSkeleton tab="roles" />}>
                      <OrganizationRolesPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Analytics section — account-level RTA analytics (requires active RTA) */}
            <Route
              path="analytics"
              element={
                <RequireActiveAccount>
                  <AccountAnalyticsPage />
                </RequireActiveAccount>
              }
            />

            {/* Data Views — account-level. Tables now live in the home / folders
               and no longer depend on an assistant, so they open here without an
               AssistantID. The assistant-nested routes below still work for the
               sidebar's per-agent table list. */}
            <Route
              path="data-views/:tableId"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.table.view}>
                  <Suspense fallback={<DataViewsPageSkeleton />}>
                    <DataViewsPlaceholder />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Accounts section */}
            <Route
              path="accounts"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.platform.accounts.viewAssigned}>
                  <AccountsLayout />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<AccountsPageSkeleton />}>
                    <Account />
                  </Suspense>
                }
              />
              <Route path="new" element={<NewAccount />} />
            </Route>

            {/* Profile section */}
            <Route
              path="profile"
              element={
                <Suspense fallback={<ProfileSkeleton />}>
                  <Profile />
                </Suspense>
              }
            />

            {/* Metrics section */}
            <Route
              path="metrics"
              element={
                <ProtectedRoute requiredRoles={['admin', 'superadmin', 'implementador']}>
                  <MetricsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
            </Route>

            {/* Modules section */}
            {/* TODO(perms): no atomic permission for "/modules" exists in the catalog
                — pending product decision. Authenticated-only for now. */}
            <Route
              path="modules"
              element={
                <ProtectedRoute>
                  <Modules />
                </ProtectedRoute>
              }
            />

            {/* Voice section — teléfonos Twilio (DEV only via REACT_APP_FEATURE_VOICE) */}
            <Route
              path="voice"
              element={
                featureFlags.voice ? (
                  <ProtectedRoute>
                    <VoiceTestPage />
                  </ProtectedRoute>
                ) : (
                  <Navigate to=".." replace />
                )
              }
            />

            {/* Assistants section */}
            {/* Agent setup flow (AGE-80) */}
            <Route
              path="agents/new"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                  <RequireActiveAccount>
                    <AgentSetupLayout />
                  </RequireActiveAccount>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="start" replace />} />
              {/* start / process / skills depend on REACT_APP_FEATURE_PROCESS_AGENTS. */}
              <Route path="start" element={agentSetupStartElement(agentSetupStartPages)} />
              {agentSetupStartRoutes(agentSetupStartPages)}
              <Route path="personality" element={<PersonalityStepPage />} />
              <Route path="behavior" element={<BehaviorStepPage />} />
              <Route path="channels" element={<ChannelsLayout />}>
                <Route index element={<ChannelsPage />} />
                <Route path=":channelId" element={<ChannelDetailPage />} />
              </Route>
              <Route path="location" element={<LocationStepPage />} />
              <Route path="access" element={<AccessStepPage />} />
            </Route>

            {/* Real Time Assistant creation — "Real Time Assistant" option in Home's
               Create new -> Agent menu. Separate from the AGE-80 wizard above:
               RTA's UploadPage renders its own header/stepper chrome.
               DEV only via REACT_APP_FEATURE_RTA_CONFIGURATION (same convention as Voice above). */}
            <Route
              path="agents/new/realtime"
              element={
                featureFlags.rtaConfiguration ? (
                  <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                    <RequireActiveAccount>
                      <RTAConfigurationPage />
                    </RequireActiveAccount>
                  </ProtectedRoute>
                ) : (
                  <Navigate to=".." replace />
                )
              }
            />

            {/* QA Agent creation wizard — "Quality Agent" option in Home's Create new ->
               Agent menu. A single wildcard route (not 7 sibling Routes) so
               QaAgentCreationWizard stays MOUNTED across step navigation —
               QaAgentWizardStepPage derives `step` from the URL itself and only
               re-renders; 7 literal sibling routes would remount the whole wizard
               (and reset its CSS-in-JS/theme setup) on every step change, since
               React Router treats a match against a DIFFERENT <Route> as a new
               component identity. DEV only via REACT_APP_FEATURE_QA_AGENT (same
               convention as the existing agentType 'qa' section further down). */}
            <Route
              path="qa/new/*"
              element={
                featureFlags.qaAgent ? (
                  <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                    <RequireActiveAccount>
                      <QaAgentWizardStepPage />
                    </RequireActiveAccount>
                  </ProtectedRoute>
                ) : (
                  <Navigate to=".." replace />
                )
              }
            />

            <Route
              path="assistants"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                  <AssistantsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AssistantManagement />} />
              <Route path="new">
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                      <RequireActiveAccount>
                        <CreateAssistantPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="behavior"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                      <RequireActiveAccount>
                        <BehaviorStep />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
                {/* Intentionally no agent.create permission here: overview redirects
                    (MyAssistantChannelsRedirect) land here with ?from=overview; access is
                    enforced inside ChannelsStep via /assistants/deploy. Wizard steps keep
                    /assistants/new on personality, behavior, location, and access. */}
                <Route
                  path="channels"
                  element={
                    <ProtectedRoute>
                      <RequireActiveAccount>
                        <ChannelsStep />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="channels/:channelId"
                  element={
                    <ProtectedRoute>
                      <RequireActiveAccount>
                        <ChannelDetailStep />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="location"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                      <RequireActiveAccount>
                        <LocationStep />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="access"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.create}>
                      <RequireActiveAccount>
                        <AccessStep />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path=":AssistantID" element={<AssistantDetailLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route
                  path="overview"
                  element={
                    <Suspense fallback={<AgentOverviewSkeleton />}>
                      <AgentOverviewPage />
                    </Suspense>
                  }
                />

                {/* The Settings tab lives in the URL so it survives refresh and
                    back/forward. A missing or unknown `:tab` is redirected to the
                    default one by the page itself. */}
                <Route
                  path="settings/:tab?"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <Suspense fallback={<AgentSettingsSkeleton />}>
                          <AgentSettingsPage />
                        </Suspense>
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                {/* Channels module (overview): same components as the Step 3
                    setup, mounted here with an agent id → mode="overview". */}
                <Route
                  path="channels"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.channels.view}>
                      <RequireActiveAccount>
                        {/* The matrix or the detail, by path (SL-1945). */}
                        <Suspense fallback={<ChannelsSkeleton />}>
                          <ChannelsLayout />
                        </Suspense>
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ChannelsPage />} />
                  <Route path=":channelId" element={<ChannelDetailPage />} />
                </Route>

                {/* Skills module (VOX-182): the feature owns its own subtree —
                    list, editor routes and execution log — in
                    features/agent-skills/router.tsx. */}
                {skillsRoutes}

                {/* Skills MVP (AGE-269), mounted under 'skills-mvp' so it can be
                    compared against the module above without either shadowing the
                    other's URLs. */}
                {skillsMvpRoutes}

                {/* Observability (AGE-357): the agent's log, owned by
                    features/agent-observability/router.tsx. */}
                {observabilityRoutes}

                <Route
                  path="inbox/chats"
                  element={
                    <RequireActiveAccount>
                      <Suspense fallback={<InboxChatsSkeleton />}>
                        <Inbox />
                      </Suspense>
                    </RequireActiveAccount>
                  }
                />

                <Route
                  path="inbox/calls"
                  element={
                    <RequireActiveAccount>
                      <InboxCallsPage />
                    </RequireActiveAccount>
                  }
                />

                {/* CX-572 — Real-Time Assistant config for an existing agent. */}
                <Route
                  path="rta/processes"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaProcessesPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rta/knowledge"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaKnowledgePage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rta/versions"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaVersionsPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rta/testing"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaTestingPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rta/settings"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaSettingsPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rta/settings/general"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaGeneralSettingsPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rta/settings/triggers"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <RequireActiveAccount>
                        <RtaTriggersSettingsPage />
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />

                {/* QA Agent (agentType 'qa') — overview real vía agent-modules.ts. Todas
                    las sub-secciones están montadas (ADR-0009); Sampling/Analytics siguen
                    sobre datos mock hasta que exista su propio cliente de backend.
                    Cada sub-sección tiene su propio permiso atómico (agent.qa.*) — el
                    gate del padre solo confirma que QA existe para esta cuenta/feature
                    flag, no reemplaza el check por módulo. `denyBehavior="inline"`
                    mantiene al usuario dentro del shell de navegación en vez de
                    mandarlo a /unauthorized. */}
                <Route
                  path="qa"
                  element={
                    featureFlags.qaAgent ? (
                      <RequireActiveAccount>
                        <QaAgentSectionGuard />
                      </RequireActiveAccount>
                    ) : (
                      <Navigate to=".." replace />
                    )
                  }
                >
                  {/* Bare `.../qa` (no sub-section) has no matching child route below,
                      so without this index the Outlet renders nothing — a blank content
                      area, not a 404 and not a redirect. Bounce it the same way the
                      feature-flag-off branch above does: up to `:AssistantID`, whose own
                      index route already redirects to `overview`. */}
                  <Route index element={<Navigate to=".." replace />} />
                  <Route
                    path="upload"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.upload.view}
                        denyBehavior="inline"
                      >
                        <QaUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="inbox"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.inbox.view}
                        denyBehavior="inline"
                      >
                        <QaInbox />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="sampling"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.sampling.view}
                        denyBehavior="inline"
                      >
                        <QaSourceSampling />
                      </ProtectedRoute>
                    }
                  />
                  {/* Evaluations — Power Skills, Scorecards y Key Moments como
                      pestañas de una sola pantalla. Sin `:tab` se entra por la
                      primera; ver `QaEvaluationsSection` para por qué la pestaña
                      vive en la ruta. */}
                  <Route path="evaluations" element={<Navigate to="power-skills" replace />} />
                  <Route
                    path="evaluations/:tab"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.evaluations.view}
                        denyBehavior="inline"
                      >
                        <QaEvaluationsSection />
                      </ProtectedRoute>
                    }
                  />
                  {/* Las dos URLs que tenían sección propia siguen funcionando:
                      entran por su pestaña en vez de romperse (enlaces guardados,
                      marcadores, el correo de alguien). No llevan `ProtectedRoute`
                      porque no renderizan nada — el gate es el de la ruta destino. */}
                  <Route
                    path="scorecards"
                    element={<Navigate to="../evaluations/scorecards" replace />}
                  />
                  <Route
                    path="key-moments"
                    element={<Navigate to="../evaluations/key-moments" replace />}
                  />
                  <Route
                    path="analytics"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.analytics.view}
                        denyBehavior="inline"
                      >
                        <QaAnalytics />
                      </ProtectedRoute>
                    }
                  />
                  {/* Training — hablar con el agente de voz conectado. Mismo permiso
                      que la card (ver `QA_MODULES`), así que la URL directa está
                      gobernada igual; y la propia página resuelve el caso "ya no hay
                      nadie conectado" sin depender de que la card se hubiera
                      ocultado. */}
                  {/* Data Views no dibuja una tabla: resuelve cuál es la del agente y
                      redirige a `data-views/:tableId`, que ya la renderiza con sus
                      filtros, su export y su RBAC. Ver QA-Assistant ADR-0012. */}
                  <Route
                    path="data-views"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.dataViews.view}
                        denyBehavior="inline"
                      >
                        <QaDataViewsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="training"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.training.view}
                        denyBehavior="inline"
                      >
                        <QaTrainingPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Knowledge Base — the platform's standard `KnowledgePage` (same as the
                      `knowledge` route above), gated by the card's permission plus
                      `agent.kb.view` (see `QaKnowledgeBaseGate`). */}
                  <Route
                    path="knowledge-base"
                    element={
                      <QaKnowledgeBaseGate>
                        <Suspense fallback={<KnowledgeSkeleton />}>
                          <Knowledge />
                        </Suspense>
                      </QaKnowledgeBaseGate>
                    }
                  />
                  {/* Settings consume datos reales (`general-config`,
                      `routing-config`, `custom-buckets`) vía el
                      QaAssistantProvider del guard, como Inbox y Evaluations.
                      Sin `:tab` en la ruta: a diferencia de Evaluations, sus
                      dos pestañas restantes están vacías, así que enlazar a una
                      en concreto no lleva a ninguna parte todavía. Cuando
                      tengan contenido, el componente ya acepta `tab` y
                      `onTabChange` para moverlas a la URL. */}
                  <Route
                    path="settings"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.qa.settings.view}
                        denyBehavior="inline"
                      >
                        <QaSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route
                  path="inbox/phone-calls"
                  element={
                    <RequireActiveAccount>
                      <InboxPhoneCallsPage />
                    </RequireActiveAccount>
                  }
                />

                <Route
                  path="inbox/agent-calls"
                  element={
                    <RequireActiveAccount>
                      <Suspense fallback={<AgentCallsSkeleton />}>
                        <InboxAgentCallsPage />
                      </Suspense>
                    </RequireActiveAccount>
                  }
                />

                <Route path="inbox" element={<InboxLegacyRedirect />} />
                <Route
                  path="data-views"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.table.view}>
                      <Suspense fallback={<DataViewsPageSkeleton />}>
                        <DataViewsPlaceholder />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="data-views/new"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.table.view}>
                      <Suspense fallback={<DataViewsPageSkeleton />}>
                        <DataViewsPlaceholder />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="data-views/:tableId"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.table.view}>
                      <Suspense fallback={<DataViewsPageSkeleton />}>
                        <DataViewsPlaceholder />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route path="management" element={<AssistantManagement />} />
                {/* Metrics/Reports section - COMMENTED OUT - NOT READY */}
                {/* <Route path="metrics" element={
                  <ProtectedRoute requiredRoles={['admin', 'superuser', 'implementador']}>
                    <MetricsLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                </Route> */}

                {/* My assistant configuration */}
                <Route
                  path="myassistant"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <MyAssistantLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="personality" replace />} />
                  <Route
                    path="personality"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.settings.view}>
                        <RequireActiveAccount>
                          <MyAssistantPersonality mode="edit" />
                        </RequireActiveAccount>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="flows"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.conversationalFlows.view}
                      >
                        <RequireActiveAccount>
                          <Flows />
                        </RequireActiveAccount>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="process"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.processFlows.view}>
                        <RequireActiveAccount>
                          <AssistantProcessFlowRoute />
                        </RequireActiveAccount>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="knowledge"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.kb.view}>
                        <RequireActiveAccount>
                          <Suspense fallback={<KnowledgeSkeleton />}>
                            <Knowledge />
                          </Suspense>
                        </RequireActiveAccount>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="channels"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.channels.view}>
                        <RequireActiveAccount>
                          <MyAssistantChannelsRedirect />
                        </RequireActiveAccount>
                      </ProtectedRoute>
                    }
                  />
                  {/* TODO(perms): no atomic permission for "appearance" in the catalog. Gated by agent access. */}
                  <Route
                    path="appearance"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                        <Appearance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="queues"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                        <QueueManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="metrics"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.metrics.view}>
                        <Suspense fallback={<MetricsSkeleton />}>
                          <MetricsDashboard />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="evals"
                    element={
                      featureFlags.evals ? (
                        <ProtectedRoute requiredPermission={PERMISSIONS.agent.evals.view}>
                          <Suspense fallback={<EvalsSkeleton />}>
                            <EvalsDashboard />
                          </Suspense>
                        </ProtectedRoute>
                      ) : (
                        <Navigate to=".." replace />
                      )
                    }
                  />
                  {/* TODO(perms): no atomic permission for "campaigns" in the catalog yet.
                      Gated by agent access; the backend re-checks against DEFAULT_ROLES. */}
                  <Route
                    path="campaigns"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                        <Suspense fallback={<CampaignRouteSkeleton kind="list" />}>
                          <CampaignsPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* Subagents (VOX-329): the catalogue Campaigns pick from, same
                      gate as the `campaigns` route — see agent-subagents/permissions.ts. */}
                  {subagentsRoutes}
                  {/* The wizard is a sibling, not a child: it replaces the
                      listing rather than rendering inside it, and the listing
                      has no <Outlet>.

                      Role-gated, not permission-gated: the PRD restricts
                      CREATING a campaign to supervisors and admins (viewing and
                      pausing stay open to anyone with agent access, which is why
                      the listing route above is not gated this way), and no
                      atomic `campaigns.*` permission exists in the catalogue yet
                      — the TODO(perms) above. `requiredRoles` is the escape
                      hatch ProtectedRoute documents for exactly that case.

                      ⚠️ This is defence in depth, never the boundary. The
                      create endpoint must re-check the role server-side; today
                      the writes go to an in-memory draft that checks nothing.
                      See docs/frontend/campaigns-architecture.md. */}
                  <Route
                    path="campaigns/new"
                    element={
                      <ProtectedRoute
                        requiredPermission={PERMISSIONS.agent.view}
                        requiredRoles={[...CAMPAIGN_MANAGE_ROLES]}
                      >
                        <Suspense fallback={<CampaignRouteSkeleton kind="create" />}>
                          <CampaignWizardPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* A campaign's detail, edited in place (VOX-317). Same gate as
                      the list: viewing, pausing and editing are open to anyone
                      with agent access;
                      completing (here) and deleting (the list's menu) are
                      role-checked on the page (domain/campaignActions.ts) and
                      again by Voice.
                      `campaigns/new` above still wins for "new": a static
                      segment outranks a dynamic one. */}
                  <Route
                    path="campaigns/:campaignId"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                        <Suspense fallback={<CampaignRouteSkeleton kind="detail" />}>
                          <CampaignDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  {/* A campaign's results table, full page (VOX-319): the
                      detail's Contacts without the height cap. Same gate as
                      the detail it comes from. */}
                  <Route
                    path="campaigns/:campaignId/table"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                        <Suspense fallback={<CampaignRouteSkeleton kind="results" />}>
                          <CampaignResultsPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="escalations"
                    element={
                      <ProtectedRoute requiredPermission={PERMISSIONS.agent.escalations.view}>
                        <RequireActiveAccount>
                          <Suspense fallback={<EscalationsSkeleton />}>
                            <EscalationsPage />
                          </Suspense>
                        </RequireActiveAccount>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="intents"
                    element={
                      featureFlags.intents ? (
                        <ProtectedRoute requiredPermission={INTENTS_ACCESS}>
                          <RequireActiveAccount>
                            <IntentsPage />
                          </RequireActiveAccount>
                        </ProtectedRoute>
                      ) : (
                        <Navigate to=".." replace />
                      )
                    }
                  />
                  {/* <Route path="team-overview" element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.view}>
                      <TeamOverview />
                    </ProtectedRoute>
                  } /> */}
                </Route>

                {/* Access & Verification */}
                {/* TODO(perms): no atomic permission for "access-verification" in the catalog. Gated by agent settings access. */}
                <Route
                  path="access-verification"
                  element={
                    <ProtectedRoute requiredPermission={PERMISSIONS.agent.settings.view}>
                      <RequireActiveAccount>
                        <Suspense fallback={<AccessVerificationSkeleton />}>
                          <AccessVerification />
                        </Suspense>
                      </RequireActiveAccount>
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>

            {/* Catch-all route for invalid paths within an account - redirect based on permissions */}
            <Route path="*" element={<SmartRedirect />} />
          </Route>

          {/* Tools Section */}
          <Route
            path="/tools/cost-calculator"
            element={
              <ProtectedRoute>
                <CostCalculatorPage />
              </ProtectedRoute>
            }
          />

          {/* Redirect root to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
